<template>
  <v-dialog v-model="historyDialog" max-width="800px">
    <v-card>
      <v-card-title class="text-h5">Training History</v-card-title>

      <!-- Tabs for Staff and Global -->
      <v-tabs v-model="activeTab" background-color="primary" dark>
        <v-tab value="staff">Staff</v-tab>
        <v-tab value="global">Global</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item key="staff">
          <v-card-text>
            <v-layout v-if="loading" class="align-center justify-center">
              <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
            </v-layout>
            <v-list v-else two-line>
              <v-list-item v-for="(history, index) in histories" :key="index">
                <v-list-item-content>
                  <v-list-item-title>{{ history.TrainingSubject }}</v-list-item-title>
                  <v-list-item-subtitle>{{ history.Date }}</v-list-item-subtitle>
                  <v-list-item-subtitle>{{ history.Description }}</v-list-item-subtitle>
                  <!-- Display associated documents if available -->
                  <div class="mt-3">
                    <template v-if="history.Document && wrapAsArray(history.Document).length > 0">
                      <div
                        v-for="(doc, docIndex) in wrapAsArray(history.Document)"
                        :key="docIndex"
                        class="mb-2"
                      >
                        <div v-if="isPDF(doc.AWSLink)">
                          <LoadPDF :pdfFile="doc.AWSLink" :pdfName="doc.Name" />
                        </div>
                        <div v-else>
                          <v-icon left>mdi-file-document</v-icon>
                          <a :href="doc.AWSLink" target="_blank">{{ doc.Name || 'View Document' }}</a>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <div>No documents available</div>
                    </template>
                  </div>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="histories.length === 0">
                <v-list-item-content>
                  <div>No history available</div>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-tab-item>


        <!-- Global History Tab: Similar structure using separate variables -->
        <v-tab-item key="global">
          <v-card-text>
            <v-layout v-if="loadingGlobal" class="align-center justify-center">
              <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
            </v-layout>
            <v-list v-else two-line>
              <v-list-item v-for="(history, index) in globalHistories" :key="index">
                <v-list-item-content>
                  <v-list-item-title>{{ history.Training }}</v-list-item-title>
                  <v-list-item-subtitle>{{ history.Category }}</v-list-item-subtitle>
                  <v-list-item-subtitle>{{ history.Description }}</v-list-item-subtitle>
                  <!-- Documents Section -->
                  <div class="mt-3">
                    <template v-if="history.Document && wrapAsArray(history.Document).length > 0">
                      <div
                        v-for="(doc, docIndex) in wrapAsArray(history.Document)"
                        :key="docIndex"
                        class="mb-2"
                      >
                        <template v-if="isPDF(doc.AWSLink)">
                          <LoadPDF :pdfFile="doc.AWSLink" :pdfName="doc.Name" />
                        </template>
                        <template v-else>
                          <template v-if="['jpg', 'jpeg', 'png'].includes(doc.type)">
                            <img :src="doc.AWSLink" alt="" width="320" height="240" />
                          </template>
                          <template v-else-if="['doc', 'docx'].includes(doc.type)">
                            <a :href="doc.AWSLink" target="_blank" download>
                              {{ doc.Name || 'View Document' }}
                            </a>
                          </template>
                          <template v-else>
                            <v-icon left>mdi-file-document</v-icon>
                            <a :href="doc.AWSLink" target="_blank">{{ doc.Name || 'View Document' }}</a>
                          </template>
                        </template>
                      </div>
                    </template>
                    <template v-else>
                      <div>No documents available</div>
                    </template>
                  </div>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="globalHistories.length === 0">
                <v-list-item-content>
                  <div>No history available</div>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="closeDialog">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import LoadPDF from "@/views/client/MedVisit/loadPDF.vue";
const url = require("../../client_config/config").url;

export default {
  name: 'TrainingHistory',
  components: { LoadPDF },
  props: {
    selectedTraining: {
      type: Object,
      required: true
    },
    historyDialog: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      activeTab: 'staff',
      histories: [],
      loading: false,
      globalHistories: [],
      loadingGlobal: false
    };
  },
  watch: {
    selectedTraining: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.Training) {
          this.fetchTrainingHistory();
          if (this.activeTab === 'global') {
            this.fetchGlobalTrainingHistory();
          }
        }
      }
    },
    activeTab(newVal) {
      if (newVal === 1) {
        this.fetchGlobalTrainingHistory();
      }
    }
  },
  methods: {

    fetchTrainingHistory() {
      if (!this.selectedTraining || !this.selectedTraining.Training) {
        console.warn("No training selected");
        return;
      }
      this.loading = true;
      axios
        .get(`${url}/inserviceTraining/history`, {
          params: { trainingSubject: this.selectedTraining.Training }
        })
        .then((res) => {
          this.histories = res.data.trainings;
        })
        .catch((error) => {
          console.error('Error fetching training history:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    fetchGlobalTrainingHistory() {
      if (!this.selectedTraining || !this.selectedTraining.Training) {
        console.warn("No training selected");
        return;
      }
      this.loadingGlobal = true;
      axios
        .get(`${url}/globalTraining/history/trainings`, {
          params: { trainingSubject: this.selectedTraining.Training }
        })
        .then((res) => {
          this.globalHistories = res.data.trainings;
        })
        .catch((error) => {
          console.error('Error fetching global training history:', error);
        })
        .finally(() => {
          this.loadingGlobal = false;
        });
    },

    wrapAsArray(doc) {
      return Array.isArray(doc) ? doc : [doc];
    },

    isPDF(link) {
      return link.split('.').pop().toLowerCase() === 'pdf';
    },

    closeDialog() {
      this.$emit('update:historyDialog', false);
      this.resetHistoryData();
    },
    resetHistoryData() {
      this.histories = [];
      this.pdfDetails = [];
      this.globalHistories = [];
      this.loading = false;
      this.loadingGlobal = false;
    }
  }
};
</script>

<style scoped>
/* Adjust styles as needed */
</style>
